const post = {
  slug: 'caching-is-easy-until-invalidation',
  number: 16,
  title: 'Caching Is Easy Until You Have to Invalidate It',
  date: '2025-10-09',
  readTime: '6 min read',
  views: 33,
  category: 'backend',
  tags: ['Backend', 'Caching', 'Performance'],
  excerpt:
    'Adding a cache is a few lines. Keeping it from serving stale data is the actual job. A practical way to think about caching so it helps you instead of quietly haunting you.',
  cover: null,
  content: `
There is an old joke that the two hard problems in computer science are naming things and cache invalidation. It survives because it is true. I once spent the better part of a day chasing a bug where a user kept seeing an old price after we had changed it. The database was correct. The API was correct. A cache three layers deep was confidently serving the old value, and nothing in the system was lying except the copy.

Adding a cache is a few lines of code and an instant win on latency. Keeping that cache from confidently serving wrong data is the part that actually takes thought, and it is where essentially all caching pain comes from.

## Why we cache, and what it costs

Caching trades freshness for speed. You keep a copy of an expensive result close at hand so you can serve it quickly instead of recomputing or refetching it every time. When reads vastly outnumber writes, which is most of the time, this is one of the biggest performance wins available, and it is cheap to add.

The cost is the copy. The moment the underlying data changes, your cached copy is a small lie waiting to be told. Everything hard about caching flows from managing the gap between the truth and the copy, and pretending that gap does not exist is how you get the bug I spent a day on.

## Expiry is the simplest answer

The easiest strategy is to let cached entries expire after a set time. You accept that data can be slightly stale for a short window, and in exchange you never have to actively manage invalidation at all. For a surprising amount of data, this is completely fine. A dashboard that is thirty seconds out of date is rarely a real problem.

The whole skill here is choosing the window honestly, per use case. How stale is genuinely acceptable for this particular data?

\`\`\`text
marketing banner    -> minutes of staleness is fine
product price        -> seconds, and even that is risky
account balance      -> usually should not be cached at all
\`\`\`

Pick the window per kind of data rather than slapping one timeout on everything, because a banner and a balance have wildly different tolerances.

## Invalidate on write when freshness matters

When stale data is genuinely not acceptable, expiry alone is not enough, and you have to actively clear the cache when the source changes. The cleanest version is to make the code that updates the data also responsible for invalidating the cached copy, so the two never drift apart for long.

\`\`\`text
update the record in the database
  -> then invalidate the cached copy for that record
next read misses the cache, fetches fresh, repopulates it
\`\`\`

The danger is partial coverage. If three different code paths can change the data and only two of them remember to invalidate, the third produces a stale cache that is maddening to debug, because the data looks correct at the source and wrong in the response. The fewer the places that can change a given piece of data, the easier this is to get right, which is one more reason to keep your write paths narrow.

## Cache the right layer

A mistake I see often is caching at the wrong level. People cache a tiny database lookup that was already fast and leave an expensive, slow computation uncached. The value of a cache is proportional to how expensive the thing behind it is. Spend your caching budget on the genuinely costly work, the slow query, the heavy aggregation, the remote call, not on things that were never the bottleneck.

## A sane default, and a checklist

For most situations, start with expiry and a deliberately chosen window, and only reach for active invalidation when the data truly cannot be stale. When I add a cache I ask:

- Is the thing behind this cache actually expensive? If not, why am I caching it?
- How stale can this specific data be, honestly?
- If it cannot be stale, every write path that changes it must invalidate it. Have I covered all of them?
- Is there one clear owner for invalidating each cached value?

Do that, and caching stays the easy win it should be, instead of becoming the haunted corner of the system where the data is right everywhere except the one place the user is looking.
`,
};

export default post;
